import { useReactiveVar } from '@apollo/client';
import {
  faAddressBook,
  faBuilding,
  faCommentAlt,
  faUser,
} from '@fortawesome/free-regular-svg-icons';
import {
  faCertificate,
  faClipboardList,
  faFile,
  faFileAlt,
  faFileInvoice,
  faNewspaper,
  faBell,
  faShirt,
  faShoppingBasket,
  faTachometerAlt,
  faUserFriends,
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

import PageSkeleton from './PageSkeleton';
import { AgencyAvatar, AgencyWrapper } from './styles';

import {
  InvoiceGeneratorIcon,
  JobMonitorIcon,
  NotificationHubIcon,
} from '@/assets/icons';
import NavigationMenu from '@/components/NavigationMenu';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import Icon from '@/elements/Icon';
import { useAppDispatch } from '@/hooks/store';
import { Role } from '@/routes/PrivateRoute';
import ROUTES from '@/routes/routes';
import { setRefetchDashboardData } from '@/store/slices/layoutSlice';
import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';
import { useFeatureValue } from '@growthbook/growthbook-react';

const Sidebar = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const currentAdmin = useReactiveVar(currentAdminVar);
  const ReleaseNotesAdmin = useFeatureValue(
    FEATURE_TOGGLE.ReleaseNotesAdmin,
    false,
  );

  const dispatch = useAppDispatch();
  const dashboardClick = () => {
    if (window.location.pathname !== ROUTES.basePath) {
      dispatch(setRefetchDashboardData(true));
    }
  };

  const adminNavigation = useMemo(
    () => [
      {
        id: 'menu-general',
        items: [
          {
            icon: faTachometerAlt,
            a11yLabel: 'Dashboard',
            to: ROUTES.basePath,
            end: true,
            id: 'dashboard-link',
            onClick: dashboardClick,
          },
          {
            icon: faCommentAlt,
            a11yLabel: 'Chat',
            to: ROUTES.inappchat,
            end: true,
            id: 'inappchat-link',
          },
          {
            icon: faCommentAlt,
            a11yLabel: 'Chat',
            to: ROUTES.chat,
            end: true,
            id: 'chat-link',
          },
          ...(currentAdmin?.role === Role.TENANT_ADMIN ||
          currentAdmin?.role === Role.HELPDESK_ADMIN
            ? [
                {
                  icon: faCommentAlt,
                  a11yLabel: 'Chat V2',
                  label: 'Chat V2 (beta)',
                  to: ROUTES.chatV2,
                  end: true,
                  id: 'chat-v2-link',
                },
                {
                  icon: faUserFriends,
                  a11yLabel: 'Chat V2 Cohort',
                  label: 'Chat V2 Cohort (beta)',
                  to: ROUTES.chatV2Cohort,
                  end: true,
                  id: 'chat-v2-cohort-link',
                },
              ]
            : []),
          {
            icon: faShirt,
            a11yLabel: 'Pending Uniforms',
            label: 'Pending Uniforms',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingUniforms}`,
            id: 'pending-uniforms-link',
          },
          {
            icon: faCertificate,
            a11yLabel: 'Pending Certificates',
            label: 'Pending Certificates',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingCertificates}`,
            id: 'pending-certificates-link',
          },
          {
            icon: faClipboardList,
            a11yLabel: 'Violations',
            label: 'Violations',
            to: ROUTES.tasks,
            id: 'accountability-link',
          },
          ...(currentAdmin?.role === Role.TENANT_ADMIN
            ? [
                {
                  icon: faClipboardList,
                  a11yLabel: 'AI Assessments',
                  to: ROUTES.aiAssessments,
                  id: 'ai-assessments-link',
                },
              ]
            : []),
        ],
      },
      {
        id: 'menu-gravywork-team',
        title: 'GravyWork Team',
        items: [
          {
            icon: faBuilding,
            a11yLabel: 'Clients',
            to: ROUTES.clients,
            id: 'clients-link',
          },
          {
            icon: faAddressBook,
            a11yLabel: 'Contacts',
            to: ROUTES.contacts,
            id: 'contacts-link',
          },
          {
            icon: faUserFriends,
            a11yLabel: 'GravyWorkers',
            to: ROUTES.workersV2,
            id: 'workers-link',
          },
        ],
      },
      {
        id: 'menu-admin',
        title: 'Admin',
        items: [
          {
            icon: faShoppingBasket,
            a11yLabel: 'Orders',
            to: ROUTES.orders,
            id: 'orders-link',
          },
          {
            icon: faFileInvoice,
            a11yLabel: 'Invoices',
            to: ROUTES.invoices,
            id: 'invoices-link',
          },
          {
            icon: faBell,
            a11yLabel: 'Invoice Health',
            to: ROUTES.invoiceHealth,
            id: 'invoice-health-link',
          },
          {
            a11yLabel: 'Invoice Generator',
            to: ROUTES.invoiceGenerator,
            id: 'inv-generator-link',
            CustomIcon: () => (
              <InvoiceGeneratorIcon fontSize="small" htmlColor="inherit" />
            ),
          },
          {
            icon: faFileAlt,
            a11yLabel: 'Payroll Runs',
            to: ROUTES.payrolls,
            id: 'payrolls-link',
          },
          {
            icon: faFile,
            a11yLabel: 'Reports',
            to: ROUTES.reports,
            id: 'reports-link',
          },
          {
            a11yLabel: 'Job Monitor',
            to: ROUTES.jobmonitor,
            id: 'jobmonitor-link',
            CustomIcon: () => (
              <JobMonitorIcon fontSize="small" htmlColor="inherit" />
            ),
          },
          ...(ReleaseNotesAdmin
            ? [
                {
                  a11yLabel: 'Notification Hub',
                  to: ROUTES.notificationHub,
                  id: 'notificationhub-link',
                  CustomIcon: () => (
                    <NotificationHubIcon fontSize="small" htmlColor="inherit" />
                  ),
                },
              ]
            : []),
          {
            icon: faNewspaper,
            a11yLabel: 'Posts',
            to: ROUTES.posts,
            id: 'posts-link',
          },
          {
            icon: faFileAlt,
            a11yLabel: 'Documents',
            to: ROUTES.documents,
            id: 'documents-link',
          },
        ],
      },
      {
        id: 'menu-my-information',
        title: 'My Information',
        items: [
          {
            icon: faUserFriends,
            a11yLabel: 'My Team',
            to: ROUTES.myTeam,
            id: 'myteam-link',
          },
          {
            icon: faBuilding,
            a11yLabel: 'My Company',
            to: ROUTES.myCompany,
            id: 'mycompany-link',
          },
          {
            icon: faUser,
            a11yLabel: 'My Profile',
            to: ROUTES.myProfile,
            id: 'profile-link',
          },
        ],
      },
    ],
    [currentAdmin, ReleaseNotesAdmin],
  );

  return (
    <Stack vertical>
      <AgencyWrapper>
        <Stack gap={16}>
          <div>
            <AgencyAvatar>
              <Icon fixedWidth icon={faBuilding} />
            </AgencyAvatar>
          </div>
          {currentAgency ? (
            <Stack vertical gap={0}>
              <Body weight="semibold">{currentAgency.name}</Body>
              <Small>Washington, DC</Small>
            </Stack>
          ) : (
            <Skeleton.Body lines={2} />
          )}
        </Stack>
      </AgencyWrapper>
      {currentAdmin ? (
        <NavigationMenu groups={adminNavigation} />
      ) : (
        <PageSkeleton />
      )}
    </Stack>
  );
};

export default Sidebar;
